/**
 * Контракты раздела «Ресурсы» (6.3 ТЗ): справочник ресурсов и ставок с версиями, назначения на работы
 * графика, потребность по дням и неделям, персонал с допусками, вахты, табели, план мобилизации,
 * стоимость ресурсов по неделям. Ставки и стоимость — с уровнем денег «Акты» и выше.
 */
import { z } from 'zod';
import type { CalendarDef } from './schedule-calendar';
import { dayNumber, dayString, parseMoment, WorkCalendar } from './schedule-calendar';

export const RESOURCE_TYPES = ['labor', 'crew', 'equipment', 'material'] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];
export const RESOURCE_TYPE_TITLES: Record<ResourceType, string> = {
  labor: 'Профессия и разряд',
  crew: 'Бригада, звено',
  equipment: 'Техника',
  material: 'Материал',
};
export const WORKER_DOC_KINDS = ['permit', 'certificate', 'medical', 'briefing'] as const;
export type WorkerDocKind = (typeof WORKER_DOC_KINDS)[number];
export const WORKER_DOC_TITLES: Record<WorkerDocKind, string> = {
  permit: 'Допуск',
  certificate: 'Удостоверение',
  medical: 'Медосмотр',
  briefing: 'Инструктаж',
};

const Day = z
  .string()
  .regex(/^(19[5-9]\d|20\d\d|21[0-4]\d)-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Дата в формате ГГГГ-ММ-ДД')
  .refine((d) => {
    // Несуществующая дата (31 февраля) даёт Invalid Date: toISOString на ней бросает исключение — это был 500
    const t = new Date(`${d}T00:00:00Z`);
    return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === d;
  }, 'Такой даты нет');
const Money = z.number().min(0).max(1e12);

export const RateRow = z.object({
  id: z.string(),
  validFrom: z.string(),
  perHour: z.number().nullable(),
  perShift: z.number().nullable(),
  payrollPct: z.number().nullable(),
  rotationPerDay: z.number().nullable(),
  lodgingPerDay: z.number().nullable(),
  mealsPerDay: z.number().nullable(),
  travelPerTrip: z.number().nullable(),
  ppePerMonth: z.number().nullable(),
  perUnit: z.number().nullable(),
  note: z.string().nullable(),
});
export type RateRow = z.infer<typeof RateRow>;

export const RateInput = z.object({
  validFrom: Day,
  perHour: Money.nullable().optional(),
  perShift: Money.nullable().optional(),
  payrollPct: z.number().min(0).max(200).nullable().optional(),
  rotationPerDay: Money.nullable().optional(),
  lodgingPerDay: Money.nullable().optional(),
  mealsPerDay: Money.nullable().optional(),
  travelPerTrip: Money.nullable().optional(),
  ppePerMonth: Money.nullable().optional(),
  perUnit: Money.nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
});

export const ResourceRow = z.object({
  id: z.string(),
  kind: z.enum(RESOURCE_TYPES),
  code: z.string().nullable(),
  name: z.string(),
  grade: z.number().nullable(),
  equipmentType: z.string().nullable(),
  model: z.string().nullable(),
  unit: z.string(),
  crew: z.array(z.object({ resourceId: z.string(), qty: z.number() })),
  requiredDocs: z.array(z.string()),
  isActive: z.boolean(),
  /** Ставки — только с уровнем денег «Акты» и выше; иначе пусто */
  rates: z.array(RateRow),
  /** Ставок нет вовсе — показывается до утверждения себестоимости */
  noRate: z.boolean(),
});
export type ResourceRow = z.infer<typeof ResourceRow>;

export const ResourceInput = z.object({
  kind: z.enum(RESOURCE_TYPES),
  code: z.string().trim().max(40).nullable().optional(),
  name: z.string().trim().min(1).max(200),
  grade: z.number().int().min(1).max(8).nullable().optional(),
  equipmentType: z.string().trim().max(120).nullable().optional(),
  model: z.string().trim().max(120).nullable().optional(),
  unit: z.string().trim().min(1).max(20).optional(),
  crew: z
    .array(z.object({ resourceId: z.uuid(), qty: z.number().min(0.1).max(1000) }))
    .max(50)
    .optional(),
  requiredDocs: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
  isActive: z.boolean().optional(),
});

export const ASSIGNMENT_CURVES = ['flat', 'front', 'back', 'bell'] as const;
export type AssignmentCurve = (typeof ASSIGNMENT_CURVES)[number];
export const ASSIGNMENT_CURVE_TITLES: Record<AssignmentCurve, string> = {
  flat: 'Равномерно',
  front: 'Больше в начале',
  back: 'Больше в конце',
  bell: 'Колоколом — пик в середине',
};
/** Вес доли работы в точке p ∈ [0, 1]; площадь под каждой кривой — 1 */
function curveWeight(c: AssignmentCurve, p: number): number {
  if (c === 'front') return 2 * (1 - p);
  if (c === 'back') return 2 * p;
  if (c === 'bell') return 6 * p * (1 - p);
  return 1;
}

export const ResourceAssignmentRow = z.object({
  id: z.string(),
  activityId: z.string(),
  resourceId: z.string(),
  units: z.number(),
  amount: z.number().nullable(),
  normPerUnit: z.number().nullable(),
  source: z.string(),
  curve: z.enum(ASSIGNMENT_CURVES).optional(),
  note: z.string().nullable(),
  /** Трудоёмкость или расход по расчёту: заданное, по норме от объёма или units × длительность */
  total: z.number(),
  /** Стоимость — с уровнем денег «Акты» и выше */
  cost: z.number().nullable(),
});
export type ResourceAssignmentRow = z.infer<typeof ResourceAssignmentRow>;

export const ResourceAssignmentInput = z.object({
  activityId: z.uuid(),
  resourceId: z.uuid(),
  units: z.number().min(0).max(10000).optional(),
  amount: z.number().min(0).max(1e10).nullable().optional(),
  normPerUnit: z.number().min(0).max(1e8).nullable().optional(),
  curve: z.enum(ASSIGNMENT_CURVES).optional(),
  note: z.string().max(1000).nullable().optional(),
});

export const WorkerDocRow = z.object({
  id: z.string(),
  kind: z.enum(WORKER_DOC_KINDS),
  name: z.string(),
  number: z.string().nullable(),
  issuedOn: z.string().nullable(),
  validUntil: z.string().nullable(),
});
export type WorkerDocRow = z.infer<typeof WorkerDocRow>;

export const WorkerRow = z.object({
  id: z.string(),
  fullName: z.string(),
  personnelNo: z.string().nullable(),
  resourceId: z.string().nullable(),
  resourceName: z.string().nullable(),
  counterpartyId: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  /** Сотрудник портала: по нему видно, что за человеком числится на складе (РС-13) */
  userId: z.string().nullable().optional(),
  userName: z.string().nullable().optional(),
  phone: z.string().nullable(),
  status: z.enum(['active', 'dismissed']),
  note: z.string().nullable(),
  docs: z.array(WorkerDocRow),
  /** Документы, которые истекли или истекают в ближайшие 30 дней, и которых не хватает по профессии */
  expired: z.array(z.string()),
  expiring: z.array(z.string()),
  missing: z.array(z.string()),
  /** Истёкшие документы, которые профессия не требует: предупреждение */
  outdated: z.array(z.string()),
});
export type WorkerRow = z.infer<typeof WorkerRow>;

export const WorkerInput = z.object({
  fullName: z.string().trim().min(2).max(200),
  personnelNo: z.string().trim().max(40).nullable().optional(),
  resourceId: z.uuid().nullable().optional(),
  counterpartyId: z.uuid().nullable().optional(),
  projectId: z.uuid().nullable().optional(),
  userId: z.uuid().nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  status: z.enum(['active', 'dismissed']).optional(),
  note: z.string().max(2000).nullable().optional(),
});

export const WorkerDocInput = z.object({
  kind: z.enum(WORKER_DOC_KINDS),
  name: z.string().trim().min(1).max(200),
  number: z.string().trim().max(80).nullable().optional(),
  issuedOn: Day.nullable().optional(),
  validUntil: Day.nullable().optional(),
});

export const RotationRow = z.object({
  id: z.string(),
  projectId: z.string(),
  workerId: z.string(),
  workerName: z.string(),
  pattern: z.string(),
  planStart: z.string(),
  planEnd: z.string(),
  actualStart: z.string().nullable(),
  actualEnd: z.string().nullable(),
  ticket: z.string().nullable(),
  lodging: z.string().nullable(),
  note: z.string().nullable(),
  /** Пересечение с другой вахтой этого человека */
  overlap: z.boolean(),
});
export type RotationRow = z.infer<typeof RotationRow>;

export const RotationInput = z.object({
  workerId: z.uuid(),
  pattern: z.string().regex(/^\d{1,2}\/\d{1,2}$/),
  planStart: Day,
  planEnd: Day,
  actualStart: Day.nullable().optional(),
  actualEnd: Day.nullable().optional(),
  ticket: z.string().max(200).nullable().optional(),
  lodging: z.string().max(200).nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
});

export const TimesheetRow = z.object({
  id: z.string(),
  workerId: z.string(),
  activityId: z.string().nullable(),
  day: z.string(),
  hours: z.number(),
  source: z.string(),
});
export const TimesheetView = z.object({
  month: z.string(),
  workers: z.array(z.object({ id: z.string(), fullName: z.string(), resourceName: z.string().nullable() })),
  entries: z.array(TimesheetRow),
  approval: z.object({ approvedBy: z.string(), approvedAt: z.string(), stale: z.boolean() }).nullable(),
  /** План и факт чел.-ч по работам графика за месяц */
  byActivity: z.array(
    z.object({
      activityId: z.string(),
      code: z.string(),
      name: z.string(),
      plan: z.number(),
      fact: z.number(),
    }),
  ),
  canEdit: z.boolean(),
  canApprove: z.boolean(),
});
export type TimesheetView = z.infer<typeof TimesheetView>;
export const TimesheetInput = z.object({
  entries: z
    .array(
      z.object({
        workerId: z.uuid(),
        activityId: z.uuid().nullable().optional(),
        day: Day,
        hours: z.number().min(0).max(24),
      }),
    )
    .max(5000),
});

export const MobilizationRow = z.object({
  id: z.string(),
  resourceId: z.string(),
  resourceName: z.string(),
  qty: z.number(),
  needBy: z.string(),
  activityId: z.string().nullable(),
  activityName: z.string().nullable(),
  kind: z.enum(['hire', 'mobilize']),
  status: z.enum(['planned', 'requested', 'done', 'cancelled']),
  note: z.string().nullable(),
});
export type MobilizationRow = z.infer<typeof MobilizationRow>;
export const MobilizationInput = z.object({
  resourceId: z.uuid(),
  qty: z.number().min(0.5).max(10000),
  needBy: Day,
  activityId: z.uuid().nullable().optional(),
  kind: z.enum(['hire', 'mobilize']).optional(),
  status: z.enum(['planned', 'requested', 'done', 'cancelled']).optional(),
  note: z.string().max(1000).nullable().optional(),
});

/** Загрузка и потребность (РС-02, РС-04): по неделям, по ресурсам */
export const LoadView = z.object({
  weeks: z.array(z.string()),
  rows: z.array(
    z.object({
      resourceId: z.string(),
      name: z.string(),
      kind: z.enum(RESOURCE_TYPES),
      unit: z.string(),
      /** Потребность: среднее число людей или машин в неделю */
      need: z.array(z.number()),
      /** Трудоёмкость или расход за неделю */
      amount: z.array(z.number()),
      /** Наличие: люди этой профессии на вахте по плану (для техники и материалов — пусто) */
      available: z.array(z.number()).nullable(),
      /** План мобилизации нарастающим итогом */
      planned: z.array(z.number()).nullable(),
    }),
  ),
  /** Стоимость по неделям: ФОТ, техника, материалы (РС-12) — с уровнем денег «Акты» */
  cost: z
    .object({ labor: z.array(z.number()), equipment: z.array(z.number()), material: z.array(z.number()) })
    .nullable(),
  /** Ресурсы без ставки на дату — пропуски до утверждения себестоимости */
  missingRates: z.array(z.string()),
});
export type LoadView = z.infer<typeof LoadView>;

/* ───────────────────────── Расчёт потребности ───────────────────────── */

/** Понедельник недели дня */
export function weekOf(day: string): string {
  const n = dayNumber(day);
  return dayString(n - ((((n + 3) % 7) + 7) % 7));
}

export interface SpreadInput {
  start: string;
  finish: string;
  calendar: CalendarDef;
  /** Всего на работу: чел.-ч, маш.-ч или расход */
  total: number;
  /** Распределение по длительности (ПЛ-15): равномерно, больше в начале, в конце, колоколом */
  curve?: AssignmentCurve;
}

/**
 * Распределение трудоёмкости работы по дням равномерно по её рабочему времени (РС-02). Возвращает
 * { день: количество }. Распределение по кривой и по факту — вторая очередь (ПЛ-15).
 */
export function spreadByDay(x: SpreadInput): Map<string, number> {
  const out = new Map<string, number>();
  const a = parseMoment(x.start);
  const b = parseMoment(x.finish);
  if (!(x.total > 0)) return out;
  const d0 = Math.floor(a / 1440);
  const d1 = Math.floor(b / 1440);
  if (d1 < d0) return out;
  const c = new WorkCalendar(x.calendar, d0 - 1, d1 - d0 + 3);
  const total = c.workAt(b) - c.workAt(a);
  if (total <= 0) {
    out.set(dayString(d0), x.total);
    return out;
  }
  const curve = x.curve ?? 'flat';
  const base = c.workAt(a);
  const raw = new Map<string, number>();
  let sum = 0;
  for (let d = d0; d <= d1; d++) {
    const from = Math.max(a, d * 1440);
    const to = Math.min(b, (d + 1) * 1440);
    const w0 = c.workAt(from) - base;
    const w = c.workAt(to) - c.workAt(from);
    if (w <= 0) continue;
    // Вес дня — по середине его рабочего времени в доле длительности работы
    const v = w * curveWeight(curve, (w0 + w / 2) / total);
    raw.set(dayString(d), v);
    sum += v;
  }
  if (sum <= 0) return out;
  for (const [d, v] of raw) out.set(d, (x.total * v) / sum);
  return out;
}

/** Ставка, действующая на дату: последняя версия с датой не позже */
export function rateOn<T extends { validFrom: string }>(rates: T[], day: string): T | null {
  let best: T | null = null;
  for (const r of rates) if (r.validFrom <= day && (!best || r.validFrom > best.validFrom)) best = r;
  return best;
}

/** Стоимость часа ресурса по ставке: час или смена (8 ч) с начислениями на ФОТ */
export function hourCost(
  r: Pick<RateRow, 'perHour' | 'perShift' | 'payrollPct'>,
  kind: ResourceType,
): number {
  const base = r.perHour ?? (r.perShift != null ? r.perShift / 8 : 0);
  return kind === 'labor' || kind === 'crew' ? base * (1 + (r.payrollPct ?? 0) / 100) : base;
}

/* ── Техника (РС-09, РС-10) ── */
export const MACHINE_OWNERSHIP = { own: 'Своя', lease: 'Лизинг', rent: 'Аренда' } as const;
export const MACHINE_STATUSES = { active: 'В работе', repair: 'В ремонте', off: 'Списана' } as const;
export const IDLE_REASONS = {
  repair: 'Ремонт',
  no_front: 'Нет фронта работ',
  no_operator: 'Нет машиниста',
  no_fuel: 'Нет топлива',
  weather: 'Погода',
  other: 'Другое',
} as const;
export const MachineRow = z.object({
  id: z.string(),
  resourceId: z.string(),
  resourceName: z.string(),
  name: z.string(),
  regNumber: z.string().nullable(),
  serialNumber: z.string().nullable(),
  ownership: z.enum(['own', 'lease', 'rent']),
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  status: z.enum(['active', 'repair', 'off']),
  hourMeter: z.number().nullable(),
  fuelNorm: z.number().nullable(),
  serviceEvery: z.number().nullable(),
  lastServiceAt: z.number().nullable(),
  /** Моточасов до ТО; меньше 0 — ТО просрочено */
  toService: z.number().nullable(),
  note: z.string().nullable(),
  /** Последнее место по телематике (РС-16) */
  position: z.object({ day: z.string(), lat: z.number(), lon: z.number() }).nullable(),
});
export type MachineRow = z.infer<typeof MachineRow>;
export const MachineInput = z.object({
  resourceId: z.uuid(),
  name: z.string().trim().min(1).max(200),
  regNumber: z.string().trim().max(50).nullable().optional(),
  serialNumber: z.string().trim().max(60).nullable().optional(),
  ownership: z.enum(['own', 'lease', 'rent']),
  ownerId: z.uuid().nullable().optional(),
  projectId: z.uuid().nullable().optional(),
  status: z.enum(['active', 'repair', 'off']).optional(),
  hourMeter: z.number().min(0).max(1e7).nullable().optional(),
  fuelNorm: z.number().min(0).max(1000).nullable().optional(),
  serviceEvery: z.number().min(0).max(1e6).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export const MachineLogInput = z.object({
  machineId: z.uuid(),
  day: Day,
  activityId: z.uuid().nullable().optional(),
  operatorId: z.uuid().nullable().optional(),
  hours: z.number().min(0).max(24),
  idleHours: z.number().min(0).max(24).optional(),
  idleReason: z
    .enum(['repair', 'no_front', 'no_operator', 'no_fuel', 'weather', 'other'])
    .nullable()
    .optional(),
  fuelLitres: z.number().min(0).max(100000).nullable().optional(),
  waybill: z.string().trim().max(50).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export const MachineLogRow = z.object({
  id: z.string(),
  machineId: z.string(),
  machineName: z.string(),
  day: z.string(),
  activityId: z.string().nullable(),
  activityCode: z.string().nullable(),
  operatorId: z.string().nullable(),
  operatorName: z.string().nullable(),
  hours: z.number(),
  idleHours: z.number(),
  idleReason: z.string().nullable(),
  fuelLitres: z.number().nullable(),
  fuelNorm: z.number().nullable(),
  waybill: z.string().nullable(),
  note: z.string().nullable(),
  /** Тот же день по телематике (РС-16) и что расходится с ручным вводом */
  telemetry: z
    .object({
      hours: z.number().nullable(),
      idleHours: z.number().nullable(),
      fuelLitres: z.number().nullable(),
      distanceKm: z.number().nullable(),
    })
    .nullable(),
  mismatch: z.array(z.enum(['hours', 'idle', 'fuel'])),
});
export type MachineLogRow = z.infer<typeof MachineLogRow>;
export const MachineServiceInput = z.object({
  day: Day,
  kind: z.enum(['to', 'repair']),
  hourMeter: z.number().min(0).max(1e7).nullable().optional(),
  cost: z.number().min(0).max(1e12).nullable().optional(),
  contractorId: z.uuid().nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
});
export const MachineReport = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(
    z.object({
      machineId: z.string(),
      name: z.string(),
      resourceName: z.string(),
      ownership: z.string(),
      hours: z.number(),
      idleHours: z.number(),
      /** Доля простоя от всего времени, % */
      idlePct: z.number().nullable(),
      fuelNorm: z.number().nullable(),
      fuelFact: z.number(),
      /** Перерасход топлива против нормы, л */
      fuelOver: z.number().nullable(),
      /** Стоимость смен по ставке справочника — с уровнем денег «Акты» */
      cost: z.number().nullable(),
      serviceCost: z.number().nullable(),
      /** По телематике за дни с путевыми листами: маш.-ч и топливо; дней, где ручной ввод расходится */
      teleHours: z.number().nullable(),
      teleFuel: z.number().nullable(),
      mismatchDays: z.number(),
    }),
  ),
  idle: z.array(z.object({ reason: z.string(), title: z.string(), hours: z.number() })),
});
export type MachineReport = z.infer<typeof MachineReport>;

/* ── Телематика техники (РС-16) ── */
export const TELEMETRY_SOURCES = { iso15143: 'ISO 15143-3 (AEMP 2.0)', csv: 'Таблица CSV' } as const;
/** Файл системы мониторинга: снимок парка ISO 15143-3 в JSON или таблица CSV по дням */
export const TelemetryImport = z.object({ text: z.string().min(1).max(5_000_000) });
export const TelemetryImportResult = z.object({
  format: z.enum(['iso15143', 'csv']),
  /** Сохранено показаний (машино-дней) */
  saved: z.number(),
  /** Номера машин из файла, которых нет в парке или нет доступа */
  unmatched: z.array(z.string()),
  /** Строк без даты или показаний */
  skipped: z.number(),
});
export type TelemetryImportResult = z.infer<typeof TelemetryImportResult>;
export const TelemetryRow = z.object({
  id: z.string(),
  machineId: z.string(),
  machineName: z.string(),
  day: z.string(),
  engineHours: z.number().nullable(),
  hours: z.number().nullable(),
  idleHours: z.number().nullable(),
  fuelLitres: z.number().nullable(),
  distanceKm: z.number().nullable(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  source: z.enum(['iso15143', 'csv']),
  /** Путевой лист за этот день уже есть */
  hasLog: z.boolean(),
});
export type TelemetryRow = z.infer<typeof TelemetryRow>;

/* ── Вахтовый посёлок (РС-15) ── */
export const CAMP_KINDS = { dorm: 'Общежитие', trailer: 'Вагон-дом', hotel: 'Гостиница' } as const;
export const CampUnitInput = z.object({
  name: z.string().trim().min(1).max(200),
  kind: z.enum(['dorm', 'trailer', 'hotel']),
  places: z.number().int().min(1).max(5000),
  note: z.string().max(1000).nullable().optional(),
});
export const CampView = z.object({
  from: z.string(),
  to: z.string(),
  units: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      kind: z.enum(['dorm', 'trailer', 'hotel']),
      places: z.number(),
      /** Живут сегодня */
      occupied: z.number(),
    }),
  ),
  /** По дням: живут, мест, по плану вахт на площадке, питание (порций), стоимость проживания и питания */
  days: z.array(
    z.object({
      day: z.string(),
      living: z.number(),
      places: z.number(),
      planned: z.number(),
      meals: z.number(),
      cost: z.number().nullable(),
    }),
  ),
  /** Живут сейчас и заезжающие без места */
  stays: z.array(
    z.object({
      id: z.string(),
      unitId: z.string(),
      unitName: z.string(),
      workerId: z.string(),
      workerName: z.string(),
      rotationId: z.string(),
      checkIn: z.string(),
      checkOut: z.string().nullable(),
    }),
  ),
  /** Вахта на площадке (заезд по факту), но не заселена */
  unhoused: z.array(z.object({ rotationId: z.string(), workerName: z.string(), actualStart: z.string() })),
  overbooked: z.array(
    z.object({
      unitId: z.string(),
      name: z.string(),
      day: z.string(),
      living: z.number(),
      places: z.number(),
    }),
  ),
});
export type CampView = z.infer<typeof CampView>;

/* ── Сетка вахт «люди × дни» (РС-06) ── */

/** Состояние дня человека: план и факт в одной клетке */
export const ROTATION_CELLS = ['none', 'plan', 'fact', 'both'] as const;
export type RotationCell = (typeof ROTATION_CELLS)[number];

export const ROTATION_ISSUE_KINDS = [
  'overlap',
  'no_arrival',
  'late_arrival',
  'late_departure',
  'early_departure',
  'fact_without_plan',
  'short_rest',
] as const;
export type RotationIssueKind = (typeof ROTATION_ISSUE_KINDS)[number];

export const RotationGridIssue = z.object({
  kind: z.enum(ROTATION_ISSUE_KINDS),
  workerId: z.string(),
  workerName: z.string(),
  rotationId: z.string().nullable(),
  /** Первая затронутая дата месяца */
  day: z.string().nullable(),
  text: z.string(),
});

export const RotationGridView = z.object({
  month: z.string(),
  /** Числа месяца: ГГГГ-ММ-ДД */
  days: z.array(z.string()),
  /** Выходные дни (суббота, воскресенье) — номера позиций в days */
  weekends: z.array(z.number()),
  today: z.string(),
  workers: z.array(
    z.object({
      workerId: z.string(),
      workerName: z.string(),
      /** Графики вахт человека в месяце */
      patterns: z.array(z.string()),
      /** Клетка на каждый день месяца: none | plan | fact | both */
      cells: z.array(z.enum(ROTATION_CELLS)),
      planDays: z.number(),
      factDays: z.number(),
    }),
  ),
  /** Людей на вахте по плану и по факту в каждый день */
  planTotals: z.array(z.number()),
  factTotals: z.array(z.number()),
  issues: z.array(RotationGridIssue),
});
export type RotationGridView = z.infer<typeof RotationGridView>;

/** Заполнение вахт по графику: циклы «работа/отдых» с первой даты заезда до конечной даты */
export const RotationFillInput = z.object({
  workerIds: z.array(z.uuid()).min(1).max(100),
  pattern: z.string().regex(/^\d{1,2}\/\d{1,2}$/),
  firstStart: Day,
  until: Day,
  ticket: z.string().max(200).nullable().optional(),
  lodging: z.string().max(200).nullable().optional(),
});
export const RotationFillResult = z.object({
  created: z.number(),
  /** Циклы, которые пропущены: у человека уже есть вахта на эти даты */
  skipped: z.number(),
});
export type RotationFillResult = z.infer<typeof RotationFillResult>;
